import { useCallback, useState } from "react";
import { Button, Icon, IconButton, Menu, useToast } from "@astrum/ui";
import type { MenuItem } from "@astrum/ui";
import { ApiError } from "../lib/api.js";
import { downloadExcel, excelFileName } from "../lib/export-excel.js";

interface ExportMenuProps {
  /** The server route, with its filters already in the query string. */
  excelPath: string;
  /** "tasks", "projects" — goes into the file name. */
  kind: string;
  /** When given, a small menu beside the button offers the quick CSV as well. */
  onCsv?: () => void | Promise<void>;
}

/**
 * "Export to Excel", with CSV tucked behind a chevron for the people who want
 * it. Excel is the primary action because it is the complete one: the server
 * writes every matching row and several sheets, where the CSV is only what the
 * screen holds.
 */
export function ExportMenu({ excelPath, kind, onCsv }: ExportMenuProps) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const run = useCallback(
    async (job: () => void | Promise<void>) => {
      if (busy) return;
      setBusy(true);
      try {
        await job();
      } catch (error) {
        toast(error instanceof ApiError ? error.message : "Could not export.", { tone: "danger" });
      } finally {
        setBusy(false);
      }
    },
    [busy, toast],
  );

  const items: MenuItem[] = onCsv ? [{ id: "csv", label: "Export as CSV", icon: "download", onSelect: () => void run(onCsv) }] : [];

  return (
    <>
      <Button
        variant="secondary"
        size="sm"
        icon={<Icon name="download" size={14} />}
        disabled={busy}
        onClick={() => void run(() => downloadExcel(excelPath, excelFileName(kind)))}
      >
        {busy ? "Exporting…" : "Export to Excel"}
      </Button>
      {onCsv && (
        <Menu open={menuOpen} onClose={() => setMenuOpen(false)} label="More export formats" items={items}>
          <IconButton
            icon={<Icon name="chevron-down" size={14} />}
            label="More export formats"
            size="sm"
            onClick={() => setMenuOpen((v) => !v)}
          />
        </Menu>
      )}
    </>
  );
}
